/* eslint-disable @typescript-eslint/no-use-before-define */

import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';

import PropertyFilter from '../../../widgets/property-filter-menu/models/property-filter';
import {
  ListOperator,
  LogicalOperator,
  PropertyType,
  ResourceType,
} from '../../../widgets/property-filter-menu/models/types';

import {CohortSegment, SavedCohortSegment} from '../segment-menu/types';

import {CohortFilterTree, CohortFilterTreeChild, BookmarkCohortFilter} from './types';

function generateCohortFilterTree(filter: PropertyFilter) {
  const cohorts = filter.filterValue as CohortSegment[];
  const negated = filter.filterOperator === ListOperator.DoesNotContain;

  return generateCohortFilterSubTree(cohorts, negated);
}

function generateCohortFilterSubTree(cohorts: CohortSegment[], negated: boolean): CohortFilterTree {
  if (!cohorts.length) {
    return null;
  }

  const cohortFilter = getFilterByCohort(cohorts[0], negated);
  const remainingCohorts = cohorts.slice(1);
  const filterByCohort = {
    operator: LogicalOperator.Or,
    children: compact([
      cohortFilter,
      remainingCohorts.length === 1
        ? getFilterByCohort(remainingCohorts[0], negated)
        : generateCohortFilterSubTree(remainingCohorts, negated),
    ]),
  };

  // @ts-ignore - TODO: Type '{ operator: LogicalOperator; children: (CohortFilterTree | { cohort: { id: number; name: string; negated: boolean; }; })[]; }' is not assignable to type 'CohortFilterTree'.
  return filterByCohort;
}

export function getFilterByCohorts(filters: PropertyFilter[]): CohortFilterTree {
  if (filters.length === 1) {
    return generateCohortFilterTree(filters[0]);
  }

  return {
    operator: LogicalOperator.And,
    children: [generateCohortFilterTree(filters[0]), getFilterByCohorts(filters.slice(1))],
  };
}

function getFilterByCohort(filter: CohortSegment, negated = false) {
  if ((filter as SavedCohortSegment).cohort.id) {
    const {cohort} = filter as SavedCohortSegment;
    return {
      cohort: {
        id: cohort.id,
        name: cohort.name,
        negated,
      },
    };
  }

  // Inline cohort
  return {
    cohort: {
      ...filter.cohort,
      negated,
    },
  };
}

function isBookmarkCohortFilter(filter: BookmarkCohortFilter | CohortFilterTree): filter is BookmarkCohortFilter {
  return filter && filter.hasOwnProperty(`cohort_id`);
}

function isBookmarkCohortTree(filter: CohortFilterTreeChild): filter is CohortFilterTree {
  return filter && filter.hasOwnProperty(`operator`);
}

/**
 * Parses filter by cohorts
 */
export function bookmarkFilterByCohortToCohortFilters(filterByCohort: string): PropertyFilter[] {
  const filters = [];
  const cohortFilter: CohortFilterTree | BookmarkCohortFilter = JSON.parse(filterByCohort || null);
  const getCohortFilter = (cohort, negated = null): CohortSegment => {
    if (cohort.raw_cohort) {
      return {
        cohort: {
          raw_cohort: cohort.raw_cohort,
          name: cohort.name,
          negated: negated !== null ? negated : cohort.negated,
        },
      };
    }

    return {
      cohort: {
        id: cohort.id || cohort.cohort_id,
        name: cohort.name,
        negated: negated !== null ? negated : cohort.negated,
      },
    };
  };

  if (isEmpty(cohortFilter)) {
    return filters;
  } else if (isBookmarkCohortFilter(cohortFilter)) {
    // handle single cohort case
    return [
      new PropertyFilter({
        filterOperator: cohortFilter.negated ? ListOperator.DoesNotContain : ListOperator.Contains,
        filterValue: [getCohortFilter(cohortFilter)],
        propertyType: PropertyType.List,
        resourceType: ResourceType.Cohort,
      }),
    ];
  }
  return collectFilterGroups(cohortFilter, true) as PropertyFilter[];

  /**
   * Traverse the cohort tree to produce distinct groups of cohort filters.
   *
   * Assumption: the high level filter groups are joined by ANDs at the top,
   * and ANDs always come above ORs in the cohort filter tree.
   */
  function collectFilterGroups(node: CohortFilterTree | CohortFilterTreeChild, isRoot = false) {
    if (isBookmarkCohortTree(node)) {
      const {operator} = node;
      let filterValues;
      if (operator === LogicalOperator.And) {
        // AND nodes produce distinct groups of CohortSegments (array of arrays)
        // concat the children as they come back
        filterValues = node.children
          .map(function mapChildrenOfAndNode(child) {
            return collectFilterGroups(child);
          })
          .reduce((arr, child) => arr.concat(child), []);
      } else if (operator === LogicalOperator.Or) {
        // OR nodes produce one group of CohortSegments (single array)
        // wrap in array on the way up so we know it's a distinct group:  [[v1, v2]]
        filterValues = [
          flatMap(node.children, function mapChildrenOfOrNode(child) {
            // response will come back wrapped in array, so unwrap with flatten
            return flatten(collectFilterGroups(child));
          }),
        ];
      } else {
        throw new Error(`Invalid cohort operator: ${operator}`);
      }

      if (isRoot) {
        return filterValues.map(toPropertyFilter);
      } else {
        return filterValues;
      }
    } else {
      // this is a leaf node, return it wrapped so it may be combined with other leaves
      return [getCohortFilter(node.cohort)];
    }
  }

  function toPropertyFilter(maybeCohortList: CohortSegment | CohortSegment[]) {
    let cohortList: CohortSegment[];
    if (!Array.isArray(maybeCohortList)) {
      cohortList = [maybeCohortList];
    } else {
      cohortList = maybeCohortList;
    }
    const shouldNegate = cohortList[0].cohort.negated;
    return new PropertyFilter({
      filterOperator: shouldNegate ? ListOperator.DoesNotContain : ListOperator.Contains,
      filterValue: cohortList.map((cohort) => {
        if (shouldNegate) {
          cohort.cohort.negated = false;
        }
        return cohort;
      }),
      propertyType: PropertyType.List,
      resourceType: ResourceType.Cohort,
    });
  }
}
